import {Component, forwardRef, Input, OnDestroy, OnInit} from '@angular/core'
import {
  NG_VALUE_ACCESSOR,
  NG_VALIDATORS,
  ControlValueAccessor,
  AbstractControl,
  FormBuilder,
  FormGroup,
} from '@angular/forms'
import {Subject, BehaviorSubject, combineLatest} from 'rxjs'
import {takeUntil, distinctUntilChanged, map} from 'rxjs/operators'
import {
  basicValueFilterTypes,
  filterTypeValue,
  ordinalFilterTypes,
  sinceTypes,
  timeFilterTypes,
} from '../search-filter.component'

@Component({
  selector: 'mypayter-portal-filter-value-form',
  templateUrl: './filter-value-form.component.html',
  styleUrls: ['./filter-value-form.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FilterValueFormComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: FilterValueFormComponent,
      multi: true,
    },
  ],
  standalone: false,
})
export class FilterValueFormComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @Input() filterType$: BehaviorSubject<filterTypeValue>
  basicValueFilterTypes = basicValueFilterTypes
  timeFilterTypes = timeFilterTypes
  ordinalFilterTypes = ordinalFilterTypes
  sinceTypes = sinceTypes
  form: FormGroup
  destroy$ = new Subject<void>()

  constructor(private fb: FormBuilder) {
    this.form = this.fb.group({
      boolValue: this.fb.control(true),
      textValue: this.fb.control(null, this.ValidateSearchFilterValue),
      dateRangeValue: this.fb.control(null, this.ValidateSearchFilterValue),
      rangeValue: this.fb.control(null, this.ValidateSearchFilterValue),
      sinceValue: this.fb.control(null),
    })
  }
  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  writeValue(obj: any): void {
    if (!obj) {
      this.form.setValue(
        {
          boolValue: true,
          textValue: null,
          dateRangeValue: null,
          rangeValue: null,
          sinceValue: null,
        },
        {emitEvent: false},
      )
    }
    if (this.filterType$.value === filterTypeValue.BOOLEAN) {
      this.form.patchValue({boolValue: !!obj}, {emitEvent: false})
    }
    if (basicValueFilterTypes.includes(this.filterType$.value)) {
      this.form.patchValue({textValue: obj}, {emitEvent: false})
    }
    if (timeFilterTypes.includes(this.filterType$.value)) {
      this.form.patchValue({dateRangeValue: obj}, {emitEvent: false})
    }
    if (ordinalFilterTypes.includes(this.filterType$.value)) {
      this.form.patchValue({rangeValue: obj}, {emitEvent: false})
    }
    if (sinceTypes.includes(this.filterType$.value)) {
      this.form.patchValue({sinceValue: obj}, {emitEvent: false})
    }
  }
  registerOnChange(fn: any): void {
    combineLatest([this.form.valueChanges, this.filterType$])
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged(),
        map(([formValues, filterType]) => {
          if (filterType === filterTypeValue.BOOLEAN) {
            return formValues.boolValue ? 'true' : 'false'
          }
          if (basicValueFilterTypes.includes(filterType)) {
            return formValues.textValue
          }
          if (timeFilterTypes.includes(filterType)) {
            return formValues.dateRangeValue
          }
          if (ordinalFilterTypes.includes(filterType)) {
            return formValues.rangeValue
          }
          if (sinceTypes.includes(filterType)) {
            return formValues.sinceValue
          }
          return null
        }),
      )
      .subscribe(fn)
  }
  registerOnTouched(fn: any): void {
    // throw new Error('Method not implemented.');
  }
  setDisabledState?(isDisabled: boolean): void {
    // throw new Error('Method not implemented.');
  }

  ngOnInit() {}
  validateMinMax(control: AbstractControl) {
    // no validation
    return null

    if (!!!control.value.amount) {
      return {error: 'Cannot be empty or 0'}
    }

    if (control.value.timeUnit !== null) {
      if (
        control.value.amount > control.value.timeUnit.max ||
        control.value.amount < control.value.timeUnit.min
      ) {
        return {
          error: `Must be between ${control.value.timeUnit.min} and ${control.value.timeUnit.max}`,
        }
      }
    }
    return null
  }
  validate(ctrl: AbstractControl) {
    if (basicValueFilterTypes.includes(this.filterType$.value)) {
      // validate basic
      return this.form.controls.textValue.errors
    }
    if (timeFilterTypes.includes(this.filterType$.value)) {
      // validate time filter
      return this.form.controls.dateRangeValue.errors
    }
    if (ordinalFilterTypes.includes(this.filterType$.value)) {
      // validate rangefilter
      return this.form.controls.rangeValue.errors
    }
    if (sinceTypes.includes(this.filterType$.value)) {
      // return since filter
      return this.form.controls.sinceValue.errors
    }
    return null
  }

  ValidateSearchFilterValue(control: AbstractControl) {
    if (!!!control.value) {
      return {error: 'Cannot be empty'}
    }
    if (
      typeof control.value === 'object' &&
      !Object.values(control.value).find((val) => val !== null)
    ) {
      return {error: 'Cannot be empty'}
    }
    if (typeof control.value === 'object') {
      const setItems = Object.entries(control.value).filter(
        ([key, item]) => !!item,
      )
      if (setItems.length === 1) {
        // only one value so no problem
        return null
      }
      if (setItems.length === 2) {
        if (control.value.hasOwnProperty('begin')) {
          // must also have an end value
          if (control.value.begin > control.value.end) {
            return {error: 'startdate must be before enddate'}
          }
        }
        if (control.value.hasOwnProperty('min')) {
          // must also have a max value
          if (control.value.min > control.value.max) {
            return {error: 'the range must be from low to high'}
          }
        }
      }
    }
    return null
  }
}
